/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Main Navigation Component
 *
 * Top navigation bar with desktop and mobile variants.
 * Refactored for modularity - extracted mobile menu and desktop links.
 */

"use client";

import Link from "next/link";
import { WaitlistDialog } from "@/components/landing/waitlist-dialog";
import { isWaitlistMode } from "@/lib/utils/waitlist-mode";
import { Menu, X } from "lucide-react";
import dynamic from "next/dynamic";
import { useCallback, useEffect, useState } from "react";
import { DesktopNavigation } from "./desktop-navigation";

// Lazy load MobileMenu - only needed on mobile and when menu is open
const MobileMenu = dynamic(
  () => import("./mobile-menu").then((mod) => ({ default: mod.MobileMenu })),
  {
    ssr: false, // Mobile menu is client-only
    loading: () => null, // No loading state needed - menu appears instantly
  },
);

export default function MainNavigation() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isClient, setIsClient] = useState(false);

  // Use CSS variables directly via Tailwind classes - no JavaScript needed
  // This eliminates expensive DOM queries (getComputedStyle) on every render
  // The CTA button uses Tailwind classes with CSS variables: bg-primary text-primary-foreground border-primary/20

  useEffect(() => {
    setIsClient(true);
  }, []);

  const toggleMobile = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  // Escape key closes menu
  useEffect(() => {
    if (!isClient) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isClient]);

  // Prevent body scroll when menu is open (nothing else – no inert, no focus trap)
  useEffect(() => {
    if (!isClient) return;
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen, isClient]);

  return (
    <nav
      className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-sm scroll-optimized"
      aria-label="Main navigation"
    >
      <div className="mx-auto flex max-w-[1280px] items-center justify-between px-4 md:px-6 lg:px-8 py-3 md:py-4">
        {/* Left: Logo (links to home) */}
        <Link
          href="/"
          className="flex flex-shrink-0 items-center gap-2 rounded-md outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Contractor Co-Pilot home"
        >
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary text-white">
            <span className="text-xs font-bold">CC</span>
          </div>
          <span className="text-sm sm:text-lg font-bold text-foreground">
            <span className="xs:hidden">Co-Pilot</span>
            <span className="hidden xs:inline">Contractor Co-Pilot</span>
          </span>
        </Link>

        {/* Right: Nav links + CTA (desktop) or menu button (mobile) */}
        <div className="flex items-center gap-2 md:gap-4">
          <DesktopNavigation />

          {/* Mobile Menu Button */}
          {isClient && (
            <button
              type="button"
              className="p-2 -mr-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 md:hidden outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              onClick={toggleMobile}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu - Lazy loaded, only rendered when open */}
      {isClient && mobileMenuOpen && (
        <MobileMenu
          isOpen={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
      )}

      {isWaitlistMode() && <WaitlistDialog />}
    </nav>
  );
}
